/**
 * Immutable paired presentation intent for every authored throw.
 *
 * These profiles never move gameplay roots. They describe how the two visual rigs compress,
 * rotate, lift and maintain hand contact while combat remains authoritative.
 */

function profile(moveId, family, values) {
  return Object.freeze({
    moveId,
    family,
    catchTicks: 28,
    executeTicks: 72,
    impactAt: 0.72,
    gripHeight: 0.66,
    gripSpread: 0.34,
    attackerLean: 0.18,
    attackerTwist: 0.16,
    attackerCrouch: 0.08,
    victimHoldLean: -0.12,
    victimHoldTwist: 0.08,
    victimApexY: 0.42,
    victimRoll: -1.05,
    victimTwist: 0.24,
    victimCompression: 0.1,
    victimShiftX: 0.06,
    ...values,
  });
}

export const THROW_MOTION_PROFILES = Object.freeze({
  'ky-throw': profile('ky-throw', 'collar-drag', {
    executeTicks: 68,
    impactAt: 0.68,
    gripHeight: 0.72,
    gripSpread: 0.26,
    attackerLean: 0.26,
    attackerTwist: -0.38,
    attackerCrouch: 0.13,
    victimHoldLean: -0.2,
    victimApexY: 0.24,
    victimRoll: -1.24,
    victimTwist: 0.2,
    victimCompression: 0.14,
    victimShiftX: 0.12,
  }),
  'ky-throw-back': profile('ky-throw-back', 'reverse-wind', {
    executeTicks: 72,
    impactAt: 0.7,
    gripHeight: 0.68,
    gripSpread: 0.42,
    attackerLean: 0.16,
    attackerTwist: 0.88,
    attackerCrouch: 0.1,
    victimHoldLean: 0.14,
    victimHoldTwist: -0.24,
    victimApexY: 0.46,
    victimRoll: 1.48,
    victimTwist: -0.66,
    victimCompression: 0.1,
    victimShiftX: -0.08,
  }),
  'ky-lowthrow': profile('ky-lowthrow', 'ankle-pick', {
    executeTicks: 64,
    impactAt: 0.62,
    gripHeight: 0.22,
    gripSpread: 0.22,
    attackerLean: 0.42,
    attackerTwist: -0.12,
    attackerCrouch: 0.34,
    victimHoldLean: 0.18,
    victimApexY: 0.38,
    victimRoll: 1.34,
    victimTwist: -0.24,
    victimCompression: 0.06,
    victimShiftX: 0.16,
  }),
  'bw-throw': profile('bw-throw', 'yard-toss', {
    executeTicks: 72,
    impactAt: 0.72,
    gripHeight: 0.62,
    gripSpread: 0.5,
    attackerLean: 0.3,
    attackerTwist: -0.48,
    attackerCrouch: 0.12,
    victimHoldLean: -0.22,
    victimHoldTwist: 0.16,
    victimApexY: 0.7,
    victimRoll: -1.42,
    victimTwist: 0.5,
    victimCompression: 0.16,
    victimShiftX: 0.1,
  }),
  'bw-throw-back': profile('bw-throw-back', 'back-girder', {
    executeTicks: 76,
    impactAt: 0.74,
    gripHeight: 0.64,
    gripSpread: 0.54,
    attackerLean: 0.18,
    attackerTwist: 0.92,
    attackerCrouch: 0.16,
    victimHoldLean: 0.2,
    victimHoldTwist: -0.3,
    victimApexY: 0.58,
    victimRoll: 1.58,
    victimTwist: -0.78,
    victimCompression: 0.16,
    victimShiftX: -0.12,
  }),
  'bw-holdiron': profile('bw-holdiron', 'hold-iron', {
    catchTicks: 56,
    executeTicks: 88,
    impactAt: 0.82,
    gripHeight: 0.55,
    gripSpread: 0.58,
    attackerLean: 0.14,
    attackerTwist: 0.08,
    attackerCrouch: 0.08,
    victimHoldLean: -0.08,
    victimHoldTwist: 0.02,
    victimApexY: 0.2,
    victimRoll: -0.42,
    victimTwist: 0.1,
    victimCompression: 0.24,
    victimShiftX: 0.08,
  }),
  'bw-hold-lift': profile('bw-hold-lift', 'hold-lift', {
    catchTicks: 20,
    executeTicks: 68,
    impactAt: 0.9,
    gripHeight: 0.48,
    gripSpread: 0.5,
    attackerLean: 0.22,
    attackerTwist: -0.18,
    attackerCrouch: 0.16,
    victimHoldLean: -0.24,
    victimApexY: 1.02,
    victimRoll: -1.08,
    victimTwist: 0.36,
    victimCompression: 0.2,
    victimShiftX: 0.12,
  }),
  'bw-hold-slam': profile('bw-hold-slam', 'hold-slam', {
    catchTicks: 20,
    executeTicks: 76,
    impactAt: 0.58,
    gripHeight: 0.5,
    gripSpread: 0.54,
    attackerLean: 0.38,
    attackerTwist: 0.32,
    attackerCrouch: 0.2,
    victimHoldLean: -0.3,
    victimApexY: 0.8,
    victimRoll: 1.48,
    victimTwist: -0.42,
    victimCompression: 0.18,
    victimShiftX: 0.1,
  }),
  'bw-pitdrop': profile('bw-pitdrop', 'pit-drop', {
    catchTicks: 68,
    executeTicks: 96,
    impactAt: 0.64,
    gripHeight: 0.46,
    gripSpread: 0.62,
    attackerLean: 0.44,
    attackerTwist: -0.42,
    attackerCrouch: 0.28,
    victimHoldLean: -0.28,
    victimHoldTwist: 0.2,
    victimApexY: 0.94,
    victimRoll: -1.62,
    victimTwist: 0.72,
    victimCompression: 0.22,
    victimShiftX: 0.1,
  }),
});

export function throwMotionFor(moveId) {
  const resolved = THROW_MOTION_PROFILES[moveId];
  if (!resolved) throw new Error(`No paired throw motion profile for "${moveId}".`);
  return resolved;
}

export function throwMotionCoverage(moves) {
  const throws = moves.filter((move) => move.isThrow);
  const missing = [];
  const families = {};
  for (const move of throws) {
    const resolved = THROW_MOTION_PROFILES[move.id];
    if (!resolved) {
      missing.push(move.id);
      continue;
    }
    families[resolved.family] = (families[resolved.family] || 0) + 1;
  }
  return Object.freeze({
    complete: missing.length === 0,
    throws: throws.length,
    profiles: Object.keys(THROW_MOTION_PROFILES).length,
    families: Object.freeze(families),
    missing: Object.freeze(missing),
  });
}
